import type { PerformanceEvent } from '../../events';

import { getOperationNameFromServerTiming, getRequestIdFromServerTiming, getResourceEntryType } from './resourceParser';

const normalizeProperties = (properties: Record<string, string | number>): Record<string, string | number> =>
  Object.keys(properties).reduce((rounded: Record<string, string | number>, key) => {
    const value = properties[key];
    if (typeof value === 'number') {
      rounded[key] = Math.round(value);
    }

    return rounded;
  }, properties);

const getJavaScriptResourceProperties = ({ name }: PerformanceResourceTiming) => ({
  bundleName: name,
});

const getGraphqlResourceProperties = (resourceEntry: PerformanceResourceTiming) => {
  const serverTiming = getServerTiming(resourceEntry);

  if (!serverTiming) {
    return {};
  }

  const operationName = getOperationNameFromServerTiming(serverTiming);
  const requestId = getRequestIdFromServerTiming(serverTiming);

  return { operationName, requestId };
};

const getServerTiming = (resourceEntry: PerformanceResourceTiming): readonly PerformanceServerTiming[] | undefined =>
  resourceEntry.serverTiming;

const getFlightingResourceProperties = (resourceEntry: PerformanceResourceTiming) => {
  const nameWithoutQueryParams = resourceEntry.name.substring(0, resourceEntry.name.indexOf('?'));

  return { name: nameWithoutQueryParams, apiName: 'GetEcsFeatureFlags' };
};

const getImageResourceProperties = (resourceEntry: PerformanceResourceTiming) => {
  const usingCompression = resourceEntry.name.includes('use_compression=true');

  return {
    payload: JSON.stringify({
      usingCompression,
    }),
  };
};

const getResourceSpecificProperties = (resourceEntry: PerformanceResourceTiming) => {
  const resourceType = getResourceEntryType(resourceEntry);

  switch (resourceType) {
    case 'GraphQL':
      return getGraphqlResourceProperties(resourceEntry);
    case 'JavaScript':
      return getJavaScriptResourceProperties(resourceEntry);
    case 'Flighting':
      return getFlightingResourceProperties(resourceEntry);
    case 'Image':
      return getImageResourceProperties(resourceEntry);
    default:
      return {};
  }
};

const getResourceTimingProperties = (resourceEntry: PerformanceResourceTiming) => {
  const {
    name,
    initiatorType,
    nextHopProtocol,
    workerStart,
    redirectStart,
    redirectEnd,
    fetchStart,
    domainLookupStart,
    domainLookupEnd,
    connectStart,
    connectEnd,
    secureConnectionStart,
    requestStart,
    responseStart,
    responseEnd,
    encodedBodySize,
    decodedBodySize,
    renderBlockingStatus,
    transferSize,
  } = resourceEntry;
  const properties = normalizeProperties({
    name,
    initiatorType,
    nextHopProtocol,
    workerStart,
    redirectStart,
    redirectEnd,
    fetchStart,
    domainLookupStart,
    domainLookupEnd,
    connectStart,
    connectEnd,
    secureConnectionStart,
    requestStart,
    responseStart,
    responseEnd,
    encodedBodySize,
    decodedBodySize,
    renderBlockingStatus,
    transferSize,
  });

  return {
    ...properties,
    ...getResourceSpecificProperties(resourceEntry),
  };
};

type GetResourcePerformanceEvent = (resourceEntry: PerformanceResourceTiming) => PerformanceEvent;
export const getResourcePerformanceEvent: GetResourcePerformanceEvent = (resourceEntry) => ({
  type: 'Performance',
  name: 'resource_timing',
  duration: resourceEntry.duration,
  startTime: resourceEntry.startTime,
  occurredAt: Date.now().toString(),
  properties: getResourceTimingProperties(resourceEntry),
});
